import * as THREE from 'three';

export function createSFoldGeometry(width: number, height: number, fullness: number, foldsCount: number, openPercent: number = 0): THREE.BufferGeometry {
    const widthSegments = 100;
    const heightSegments = 20;
    
    // As openPercent goes from 0 to 1, the curtain compresses horizontally
    const actualWidth = width * (1 - openPercent * 0.8); // compresses down to 20% width when fully open
    const amplitude = (width * fullness) / foldsCount * (0.2 + openPercent * 0.3);

    const geometry = new THREE.PlaneGeometry(actualWidth, height, widthSegments, heightSegments);
    const position = geometry.attributes.position;
    
    for (let i = 0; i < position.count; i++) {
        const u = (position.getX(i) + actualWidth / 2) / actualWidth;
        const v = (position.getY(i) + height / 2) / height;
        
        // s-fold is a sinusoidal wave
        const z = Math.sin(u * foldsCount * Math.PI * 2) * amplitude * (1 - 0.1 * v);
        
        position.setZ(i, z);
    }
    
    geometry.computeVertexNormals();
    return geometry;
}

export function createPinchPleatGeometry(width: number, height: number, pleatCount: number, openPercent: number = 0): THREE.BufferGeometry {
    const widthSegments = 120;
    const heightSegments = 20;
    const actualWidth = width * (1 - openPercent * 0.7);
    
    const geometry = new THREE.PlaneGeometry(actualWidth, height, widthSegments, heightSegments);
    const position = geometry.attributes.position;
    
    for (let i = 0; i < position.count; i++) {
        const u = (position.getX(i) + actualWidth / 2) / actualWidth;
        const v = (position.getY(i) + height / 2) / height;
        
        // pinch pleat is grouped at top (v=1) and spreads down (v=0)
        let z = 0;
        const phase = u * pleatCount * Math.PI * 2;
        // complex wave that pinches at top
        const pinchStrength = Math.pow(v, 2.0); // more pinched at top
        
        // base wave
        const baseWave = Math.sin(phase);
        // pinch effect
        z = baseWave * 0.05 * (1 - pinchStrength) + Math.pow(Math.sin(phase / 2), 4) * 0.1 * pinchStrength;
        
        position.setZ(i, z);
    }
    
    geometry.computeVertexNormals();
    return geometry;
}

export function createEyeletGeometry(width: number, height: number, ringsCount: number, openPercent: number = 0): THREE.BufferGeometry {
    const widthSegments = 100;
    const heightSegments = 20;
    const actualWidth = width * (1 - openPercent * 0.75);
    const amplitude = 0.08 * (1 + openPercent);
    
    const geometry = new THREE.PlaneGeometry(actualWidth, height, widthSegments, heightSegments);
    const position = geometry.attributes.position;
    
    for (let i = 0; i < position.count; i++) {
        const u = (position.getX(i) + actualWidth / 2) / actualWidth;
        
        // eyelet wave
        const z = Math.sin(u * ringsCount * Math.PI) * amplitude;
        position.setZ(i, z);
    }
    
    geometry.computeVertexNormals();
    return geometry;
}

export function createPencilPleatGeometry(width: number, height: number, denseFolds: number, openPercent: number = 0): THREE.BufferGeometry {
    const widthSegments = 200;
    const heightSegments = 20;
    const actualWidth = width * (1 - openPercent * 0.6);
    const amplitude = 0.02 * (1 + openPercent * 2);
    
    const geometry = new THREE.PlaneGeometry(actualWidth, height, widthSegments, heightSegments);
    const position = geometry.attributes.position;
    
    for (let i = 0; i < position.count; i++) {
        const u = (position.getX(i) + actualWidth / 2) / actualWidth;
        
        // pencil pleat high frequency wave
        const z = Math.sin(u * denseFolds * Math.PI * 2) * amplitude;
        position.setZ(i, z);
    }
    
    geometry.computeVertexNormals();
    return geometry;
}

export function createTrackOrRodMesh(trackWidth: number, style: 'architectural' | 'decorative', mountType: 'wall' | 'ceiling'): THREE.Group {
    const group = new THREE.Group();
    
    if (style === 'architectural') {
        // Slimline extrusion
        const geometry = new THREE.BoxGeometry(trackWidth + 0.1, 0.02, 0.02);
        const material = new THREE.MeshStandardMaterial({ color: 0xeeeeee, metalness: 0.8, roughness: 0.2 });
        const track = new THREE.Mesh(geometry, material);
        group.add(track);
        
        // Brackets
        const bracketGeo = new THREE.BoxGeometry(0.01, 0.03, mountType === 'wall' ? 0.05 : 0.02);
        const bracketMat = new THREE.MeshStandardMaterial({ color: 0xcccccc });
        
        const b1 = new THREE.Mesh(bracketGeo, bracketMat);
        b1.position.set(-trackWidth/2 + 0.1, mountType === 'ceiling' ? 0.02 : 0, mountType === 'wall' ? -0.02 : 0);
        
        const b2 = new THREE.Mesh(bracketGeo, bracketMat);
        b2.position.set(trackWidth/2 - 0.1, mountType === 'ceiling' ? 0.02 : 0, mountType === 'wall' ? -0.02 : 0);
        
        group.add(b1, b2);
    } else {
        // Decorative pole
        const geometry = new THREE.CylinderGeometry(0.015, 0.015, trackWidth + 0.2, 16);
        geometry.rotateZ(Math.PI / 2);
        const material = new THREE.MeshStandardMaterial({ color: 0xd4af37, metalness: 0.9, roughness: 0.1 }); // Gold/Brass
        const rod = new THREE.Mesh(geometry, material);
        group.add(rod);
        
        // Finials (ends)
        const finialGeo = new THREE.SphereGeometry(0.03, 16, 16);
        const f1 = new THREE.Mesh(finialGeo, material);
        f1.position.set(-trackWidth/2 - 0.1, 0, 0);
        
        const f2 = new THREE.Mesh(finialGeo, material);
        f2.position.set(trackWidth/2 + 0.1, 0, 0);
        
        group.add(f1, f2);
    }
    
    return group;
}
